import React from "react";
//I18n
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";

// UI
import {
  Wizard,
  WizardHeader,
  WizardContent,
  WizardNav,
} from "@Shared/Components/Layouts/Wizard/Wizard";
import { Container } from "@UI/Components/Container/Container";
import { Match, On, Other } from "@Libs/Utils/Components/Match/Match";
import { Text } from "@UI/Components/Text/Text";
import { Box } from "@UI/Components/Box/Box";
import { Icon } from "@UI/Components/Icon/Icon";
import { Flex } from "@UI/Components/Flex/Flex";
import { Navigate } from "@Libs/Router/Components/Navigate/Navigate";
import { SolidInfoCircle } from "@UI/Components/Icons/SolidInfoCircle";
import { LoadingScreen } from "@UI/Components/LoadingScreen/LoadingScreen";
import { useRepeatRates } from "../hooks/useRepeatRates";
import { BackLink } from "@Shared/Components/BackLink/BackLink";
import { useNavigate } from "@Libs/Router/hooks/useNavigate";
import { ImproveAndGoal } from "./Common/ImproveAndGoal";
import { RemainingEffortNotice } from "./Common/RemainingEffortNotice";
import { TrackingsInfos } from "./Common/TrackingsInfos";
import { useCurrentUser } from "@modules/Authentication/hooks/useCurrentUser";
import { Heading } from "@UI/Components/Heading/Heading";
import { IErrorResponse } from "@api";
import messages from "./messages";
import { useRoutes } from "@Libs/Router/hooks/useRoutes";
import { Link } from "@UI/Components/Link/Link";
import { useFeatureFlag } from "@Libs/FeatureFlags/hooks/useFeatureFlag";

export const RepeatRate = () => {
  const navigate = useNavigate();
  const { generateRoute } = useRoutes();
  const { isEnabled: isAdditionalSuperTalentMetricsEnabled } = useFeatureFlag(
    "additional_supertalent_metrics"
  );

  const { currentUser } = useCurrentUser({
    includes: ["serviceProvider"],
  });

  const {
    data: repeatRateMetrics,
    isLoading,
    error,
  } = useRepeatRates(currentUser?.serviceProvider?.id);

  if (isLoading) {
    return <LoadingScreen />;
  }

  const onLeave = () => {
    navigate(["SUPERTALENT_OBJECTIVES"]);
  };

  const handleOnClick = () => {
    return generateRoute("ACADEMY_RECURRENCY");
  };

  return (
    <Match value={(error as IErrorResponse)?.response?.status}>
      <On valueBetween={[400, 499]}>
        <Navigate to="NOT_FOUND" reload />
      </On>

      <On valueBetween={[500, 599]}>
        <Navigate to="SERVER_ERROR" reload />
      </On>

      <Other>
        <Wizard paddingTop={{ base: 5, md: 8 }}>
          <WizardNav>
            <BackLink onClick={onLeave} />
          </WizardNav>
          <WizardHeader marginBottom={{ base: 5, md: 3 }}>
            <Container
              px={{ base: 0 }}
              maxW={[
                "container.md",
                "container.sm",
                "container.md",
                "container.xl",
              ]}
            >
              <Heading
                textAlign="start"
                fontSize={{ base: "3xl", lg: "5xl" }}
              >
                <FormattedMessage {...messages.repeatRateTitle} />
              </Heading>
            </Container>
          </WizardHeader>
          <WizardContent marginBottom={{ base: 5, md: 3 }}>
            <Container
              px={{ base: 0 }}
              maxW={[
                "container.md",
                "container.sm",
                "container.md",
                "container.xl",
              ]}
            >
              <Box
                borderBottomWidth="1px"
                borderBottomColor="gray.300"
                pb={{ base: 5, md: 8 }}
              >
                <Box pb={{ base: 5, md: 8 }}>
                  <Text as="span" fontSize={{ base: "md", md: "lg" }}>
                    <FormattedMessage {...messages.repeatRateInfos} />
                  </Text>
                </Box>
                {isAdditionalSuperTalentMetricsEnabled &&
                  repeatRateMetrics?.remainingEffort !== undefined && (
                    <RemainingEffortNotice
                      context="repeat_rate"
                      remainingEffort={repeatRateMetrics.remainingEffort}
                    />
                  )}
                <TrackingsInfos metrics={repeatRateMetrics} />
              </Box>
              <Box pt={{ base: 5, md: 8 }}>
                <ImproveAndGoal context="repeat_rate" />
              </Box>
              <Link as="a" href={handleOnClick()}>
                <Flex
                  cursor="pointer"
                  alignItems="center"
                  justifyContent="center"
                  mt={{ base: 5, md: 8 }}
                >
                  <Icon
                    as={SolidInfoCircle}
                    marginRight="4px"
                    color="gray.500"
                  />
                  <Text
                    fontSize="md"
                    color="gray.500"
                    textDecoration="underline"
                    mb={0}
                  >
                    <FormattedMessage {...messages.discover_more} />
                  </Text>
                </Flex>
              </Link>
            </Container>
          </WizardContent>
        </Wizard>
      </Other>
    </Match>
  );
};
